/**
 * planSwitch.ts v1.0 (2026-09-29, cc_tasks row 251, T290)
 *
 * A MentionFox subscriber who clicks a plan button gets a PLAN SWITCH, never a second
 * subscription. create-checkout refuses a new plan Checkout for a live subscriber with
 * 409 { code: "already_subscribed_use_switch" }; every plan button goes through planUrl() here,
 * which turns that answer into the switch: a Stripe billing portal page that moves the existing
 * subscription to the chosen plan (Stripe shows the prorated amount, the buyer confirms) and then
 * returns them to the page they came from with ?upgrade=switched.
 */
import { supabase } from "@/integrations/supabase/client";

export const ALREADY_SUBSCRIBED = "already_subscribed_use_switch";

type CheckoutReply = { status: number; data: any };

/** create-checkout, with the body of a non-2xx answer read back instead of thrown away. */
export async function callCreateCheckout(body: Record<string, unknown>): Promise<CheckoutReply> {
  const { data, error } = await supabase.functions.invoke("create-checkout", { body });
  if (!error) return { status: 200, data };
  const ctx: any = (error as any).context;
  if (ctx && typeof ctx.status === "number") {
    let parsed: any = null;
    try { parsed = await ctx.clone().json(); } catch { /* not JSON */ }
    return { status: ctx.status, data: parsed ?? { error: error.message } };
  }
  return { status: 0, data: { error: error.message || "The request could not be sent." } };
}

export interface PlanStatus { subscribed: boolean; plan?: string; cycle?: "monthly" | "annual" | null; status?: string }

/** The signed-in user's live MentionFox plan subscription, read from Stripe by the server. */
export async function getPlanStatus(): Promise<PlanStatus | null> {
  const r = await callCreateCheckout({ action: "plan_status" });
  return r.status === 200 && r.data && typeof r.data.subscribed === "boolean" ? (r.data as PlanStatus) : null;
}

/** The plan switch page for a subscriber: { url } to open, or { current: true } for their own plan. */
export async function switchPlanUrl(plan: string, returnTo: string, cycle?: "monthly" | "annual"): Promise<{ url: string | null; current: boolean }> {
  const r = await callCreateCheckout({ action: "switch_plan", plan, returnTo, ...(cycle ? { cycle } : {}) });
  if (r.status === 200 && r.data?.current) return { url: null, current: true };
  if (r.status === 200 && r.data?.url) return { url: String(r.data.url), current: false };
  throw new Error(r.data?.error || "The plan switch could not be opened.");
}

/**
 * Where a plan button should send this user: a new Checkout for a non-subscriber, the plan
 * switch for a subscriber (also when the button did not know they were one and the server said
 * so with 409). `switched` is true when the answer is the switch, not a new Checkout.
 */
export async function planUrl(
  body: { plan: string; cycle?: "monthly" | "annual"; returnTo?: string; [k: string]: unknown },
): Promise<{ url: string | null; switched: boolean; current: boolean }> {
  const r = await callCreateCheckout(body);
  if (r.status === 200 && r.data?.url) return { url: String(r.data.url), switched: false, current: false };
  if (r.status === 409 && r.data?.code === ALREADY_SUBSCRIBED) {
    const back = body.returnTo || (window.location.pathname + window.location.search);
    const s = await switchPlanUrl(body.plan, back, body.cycle);
    return { url: s.url, switched: true, current: s.current };
  }
  throw new Error(r.data?.error || "Checkout could not be opened.");
}
